import { useEffect, useRef, useState } from 'react';
import { mensagemDoErro } from '../../api/erros.js';
import { dispararCargaDeLotes, obterCargaDeLotes } from '../../api/lotes.js';
import type { CargaDeLotes } from '../../api/lotes.js';
import type { Consulta } from '../../api/useConsulta.js';
import { useConsulta } from '../../api/useConsulta.js';
import { Botao } from '../../componentes/Botao.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';

/** Mesmo ritmo da carga inicial: cinco segundos enquanto a leitura roda. */
export const INTERVALO_DA_CARGA_DE_LOTES_MS = 5_000;

/**
 * A carga de lotes (`GET /sincronizacao/lotes/carga`) — a verdade sobre a
 * etapa Lotes depois que a carga inicial terminou. Só pergunta com `ativa`
 * (carga inicial concluída), e pergunta de novo enquanto a leitura roda;
 * quando para de rodar, avisa quem chama para o resumo acompanhar.
 */
export function useCargaDeLotes(ativa: boolean, aoTerminar: () => void): Consulta<CargaDeLotes> {
  const consulta = useConsulta(ativa ? obterCargaDeLotes : null);
  const { recarregar } = consulta;
  const rodando = consulta.dados?.emExecucao === true;
  const rodavaAntes = useRef(false);
  const terminar = useRef(aoTerminar);
  terminar.current = aoTerminar;

  useEffect(() => {
    if (!rodando) {
      return;
    }

    const relogio = setInterval(recarregar, INTERVALO_DA_CARGA_DE_LOTES_MS);
    return () => {
      clearInterval(relogio);
    };
  }, [rodando, recarregar]);

  useEffect(() => {
    if (rodavaAntes.current && !rodando) {
      terminar.current();
    }
    rodavaAntes.current = rodando;
  }, [rodando]);

  return consulta;
}

type Props = {
  /** Depois do disparo: pergunta de novo pela carga de lotes. */
  readonly aoDisparar: () => void;
};

/**
 * "Carregar lotes do ERP", junto da linha Lotes: dispara a carga avulsa
 * (`POST /sincronizacao/lotes/carga`) depois de uma confirmação. A recusa
 * (`CARGA_DE_LOTES_EM_ANDAMENTO`, `CARGA_INICIAL_NAO_CONCLUIDA`…) aparece
 * traduzida dentro do diálogo, que não fecha.
 */
export function CarregarLotes({ aoDisparar }: Props) {
  const [confirmando, definirConfirmando] = useState(false);
  const [agindo, definirAgindo] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  function disparar() {
    definirAgindo(true);
    definirErro(null);

    dispararCargaDeLotes()
      .then(() => {
        definirAgindo(false);
        definirConfirmando(false);
        aoDisparar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirErro(mensagemDoErro(falha));
      });
  }

  return (
    <>
      <Botao
        altura="pequena"
        onClick={() => {
          definirConfirmando(true);
        }}
      >
        Carregar lotes do ERP
      </Botao>

      {confirmando && (
        <DialogoConfirmacao
          titulo="Carregar os lotes do ERP?"
          rotuloConfirmar="Carregar lotes"
          variante="primario"
          agindo={agindo}
          aoConfirmar={disparar}
          aoCancelar={() => {
            definirConfirmando(false);
            definirErro(null);
          }}
        >
          Os lotes com saldo de todos os depósitos ativos são lidos do ERP em segundo plano. O saldo
          por lote é sobrescrito com o do ERP — não vira divergência. Nada é escrito no ERP.
          {erro !== null && (
            <span className="sincronizacao__erro-dialogo" role="alert">
              {erro}
            </span>
          )}
        </DialogoConfirmacao>
      )}
    </>
  );
}
